/**
 * 「WBS 清單」匯出的共用資料列。
 *
 * CSV 與 XLSX 兩種輸出共用同一份列資料，欄位順序與換算規則只寫在這裡一次；
 * 兩邊各自決定標題文字與儲存格型別（CSV 全部是文字，XLSX 的數字要是真數字，
 * 否則 Excel 會顯示成靠左的文字、無法加總）。
 *
 * 純函式：不得 import 任何 Tauri、瀏覽器或 store API。
 */

import { buildProjectIndex, computeCost, type Dependency, type Project } from '@bigint-project/core';

export interface WbsExportRow {
  readonly wbs: string;
  readonly name: string;
  /** 1 為最上層；XLSX 用它做任務名稱縮排。 */
  readonly outlineLevel: number;
  /** ISO wall-clock，例 `2026-07-24T08:00:00`。 */
  readonly start: string;
  readonly finish: string;
  readonly durationHours: number;
  readonly percentComplete: number;
  readonly workHours: number;
  readonly cost: number;
  /** 以 `; ` 分隔的資源名稱；無指派時為空字串。 */
  readonly resources: string;
  /** 以 `; ` 分隔的前置任務，例 `1.2`、`1.2SS`；無前置時為空字串。 */
  readonly predecessors: string;
}

function hours(minutes: number): number {
  return Math.round((minutes / 60) * 100) / 100;
}

/**
 * 前置任務標籤,例如 `1.2`、`1.2SS+120m`。
 *
 * 與畫面上的前置任務欄同一套規則:FS 是預設關係,省略型別代碼(見
 * `components/grid/predecessor.ts` 的 formatPredecessorList)。
 */
function predecessorLabel(dependency: Dependency, taskWbsById: ReadonlyMap<string, string>): string {
  const predecessor = taskWbsById.get(dependency.predecessorId as string) ?? (dependency.predecessorId as string);
  const type = dependency.type === 'FS' ? '' : dependency.type;
  const lag = dependency.lagMinutes as number;
  if (lag === 0) return `${predecessor}${type}`;
  const suffix = dependency.lagIsPercent ? `${lag / 100}%` : `${lag}m`;
  return `${predecessor}${type}${lag > 0 ? '+' : ''}${suffix}`;
}

/** 依大綱順序（與畫面上的任務表格同一個順序）攤平整份專案的 WBS 清單。 */
export function buildWbsExportRows(project: Project): readonly WbsExportRow[] {
  const index = buildProjectIndex(project);
  const costByTaskId = new Map(computeCost({ project }).byTask.map((cost) => [cost.taskId as string, cost.cost as number]));
  const resourceNameById = new Map(project.resources.map((resource) => [resource.id as string, resource.name]));
  const taskWbsById = new Map(project.tasks.map((task) => [task.id as string, task.wbs]));

  return index.outlineOrder.map((taskId) => {
    const task = index.tasksById.get(taskId);
    if (task === undefined) throw new Error(`Task ${taskId} is absent from its project index.`);
    const cost = costByTaskId.get(taskId);
    if (cost === undefined) throw new Error(`Task ${taskId} has no cost result.`);
    const assignments = index.assignmentsByTaskId.get(taskId) ?? [];
    const resources = [...new Set(assignments.map((assignment) => resourceNameById.get(assignment.resourceId as string) ?? (assignment.resourceId as string)))].join('; ');

    return {
      wbs: task.wbs,
      name: task.name,
      outlineLevel: task.outlineLevel,
      start: task.start as string,
      finish: task.finish as string,
      durationHours: hours(task.durationMinutes as number),
      percentComplete: task.percentComplete as number,
      workHours: hours(task.work as number),
      cost,
      resources,
      predecessors: (index.predecessorsByTaskId.get(taskId) ?? [])
        .map((dependency) => predecessorLabel(dependency, taskWbsById))
        .join('; '),
    };
  });
}
